/**
 * One scene on the timeline: click to edit its page, drag to reorder,
 * drag the right edge to change its duration.
 */

import { useRef } from 'react';
import type { ProjectPage, SceneCue, VideoScene } from '@canvas/shared-types';
import { useVideoStore } from '@/stores/videoStore';

/** Duration drags snap to this grid so values stay readable. */
const RESIZE_SNAP_MS = 100;

interface SceneClipProps {
  page: ProjectPage;
  scene: VideoScene;
  cue: SceneCue;
  pxPerSec: number;
  thumb?: string;
  active: boolean;
  /** Playhead is inside this scene. */
  live: boolean;
  dragging: boolean;
  onSelect: (pageId: string) => void;
  onRename: (pageId: string, name: string) => void;
  onDragStart: (pageId: string) => void;
  onDragEnd: () => void;
  onDropOn: (targetPageId: string, e: React.DragEvent) => void;
}

export default function SceneClip({
  page,
  scene,
  cue,
  pxPerSec,
  thumb,
  active,
  live,
  dragging,
  onSelect,
  onRename,
  onDragStart,
  onDragEnd,
  onDropOn,
}: SceneClipProps) {
  const checkpoint = useVideoStore((s) => s.checkpoint);
  const setSceneDuration = useVideoStore((s) => s.setSceneDuration);
  const resizeRef = useRef<{ startX: number; startMs: number } | null>(null);

  const left = (cue.startMs / 1000) * pxPerSec;
  const width = Math.max(8, (scene.durationMs / 1000) * pxPerSec);
  const overlapPx = (cue.overlapInMs / 1000) * pxPerSec;

  return (
    <div
      className={`vt-scene-clip ${active ? 'active' : ''} ${live ? 'live' : ''} ${
        dragging ? 'dragging' : ''
      }`}
      style={{ left, width, zIndex: cue.index + 1 }}
      draggable
      role="button"
      tabIndex={0}
      aria-label={`${page.name}, ${(scene.durationMs / 1000).toFixed(1)} seconds`}
      title={`${page.name} · drag to reorder · drag right edge to change duration · double-click rename`}
      onClick={() => onSelect(page.pageId)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSelect(page.pageId);
      }}
      onDoubleClick={() => {
        const next = window.prompt('Scene name', page.name);
        if (next && next.trim()) onRename(page.pageId, next.trim());
      }}
      onDragStart={(e) => {
        // A resize gesture also starts on this element; it must not become a reorder.
        if (resizeRef.current) {
          e.preventDefault();
          return;
        }
        onDragStart(page.pageId);
        e.dataTransfer.setData('text/plain', page.pageId);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDropOn(page.pageId, e);
      }}
    >
      {overlapPx > 0 && (
        <span className="vt-scene-overlap" style={{ width: overlapPx }} aria-hidden />
      )}
      {thumb ? (
        <img className="vt-scene-thumb" src={thumb} alt="" draggable={false} />
      ) : (
        <span className="vt-scene-index">{cue.index + 1}</span>
      )}
      <span className="vt-scene-meta">
        <span className="vt-scene-name">{page.name}</span>
        <span className="vt-scene-duration">{(scene.durationMs / 1000).toFixed(1)}s</span>
      </span>
      <span
        className="vt-scene-resize"
        aria-hidden
        draggable={false}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => {
          e.stopPropagation();
          e.currentTarget.setPointerCapture(e.pointerId);
          resizeRef.current = { startX: e.clientX, startMs: scene.durationMs };
          checkpoint();
        }}
        onPointerMove={(e) => {
          const r = resizeRef.current;
          if (!r) return;
          const raw = r.startMs + ((e.clientX - r.startX) / pxPerSec) * 1000;
          const snapped = Math.round(raw / RESIZE_SNAP_MS) * RESIZE_SNAP_MS;
          setSceneDuration(page.pageId, snapped, { record: false });
        }}
        onPointerUp={(e) => {
          resizeRef.current = null;
          e.currentTarget.releasePointerCapture(e.pointerId);
        }}
        onPointerCancel={() => {
          resizeRef.current = null;
        }}
      />
    </div>
  );
}
